import { safeLocalStorage } from "@/lib/storage";

/* ---------------------------------------------------------------------------
 * State shared between the new-session skeleton (SessionSkeleton.tsx) and the
 * real NewSessionView:
 *
 * - a handoff for text typed into the skeleton's live composer while the app
 *   is still loading, consumed by the real composer once it has mounted.
 * -------------------------------------------------------------------------*/

const COMPOSER_HANDOFF_KEY = "codesmith-skeleton-composer-handoff";
// Long enough to span a slow cold load; short enough that text abandoned by
// a navigation away mid-load doesn't resurface on a much later visit.
const COMPOSER_HANDOFF_TTL_MS = 5 * 60 * 1000;

/** `null` when there is no live handoff; `""` when the user emptied the box. */
function readComposerHandoff(): string | null {
  const raw = safeLocalStorage.get(COMPOSER_HANDOFF_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { text?: string; timestamp?: number };
    if (
      typeof parsed.text !== "string" ||
      typeof parsed.timestamp !== "number" ||
      Date.now() - parsed.timestamp > COMPOSER_HANDOFF_TTL_MS
    ) {
      return null;
    }
    return parsed.text;
  } catch {
    return null;
  }
}

/**
 * Persist text typed into the skeleton composer while the app loads. Emptied
 * text is kept as `""` so the real composer knows the user cleared it and
 * neither shows nor auto-sends a `?prompt=` prefill instead.
 */
export function saveSkeletonComposerText(text: string) {
  safeLocalStorage.set(
    COMPOSER_HANDOFF_KEY,
    JSON.stringify({ text, timestamp: Date.now() }),
  );
}

/**
 * Current handoff text, left in place. Safe to call during render; see
 * NewSessionView for why render must not consume it.
 */
export function peekSkeletonComposerText(): string | null {
  return readComposerHandoff();
}

/**
 * Consume the handoff. Called by NewSessionView once the real composer has
 * mounted, when the skeleton can no longer receive keystrokes.
 */
export function takeSkeletonComposerText(): string | null {
  const text = readComposerHandoff();
  safeLocalStorage.remove(COMPOSER_HANDOFF_KEY);
  return text;
}
